import React, { useEffect, useMemo, useState } from 'react';
import Header from '../../components/common/global-top-bar';
import SwitchBar from '../../components/switch-bar';
import { PEER_NAVIGATION } from '../peer-listing/peer-constants';
import { ScheduleHistoryParams } from '../../hooks/useScheduleReporting';
import { Spinner } from '../../components/common';
import { usePeers } from '../../hooks/usePeers';
import ChatbotMessageTable from './chat-messages-table';
import { CrisisState, useChatbotMessages } from '../../hooks/useChatBot';
import ScheduleFilters from '../../components/common/schedule-filter';
import ChatbotSummaryTable from './chat-summaries-table';

const PeersonaChatLogs: React.FC = () => {
  const today = new Date();
  const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const [peerCrisisState, setPeerCrisisState] = useState<CrisisState | undefined>(undefined);
  const { data: peers, isLoading: isPeersLoading } = usePeers({
    userType: ['peer'],
    limit: 1000,
  });

  const [scheduleHistoryFilters, setScheduleHistoryFilters] = useState<ScheduleHistoryParams>({
    listener_role_ids: [],
    start_date: firstDayOfCurrentMonth.toISOString().split('T')[0],
    end_date: lastDayOfCurrentMonth.toISOString().split('T')[0],
  });

  const { data: chatbotMessages, isLoading: isChatbotMessagesLoading } = useChatbotMessages({
    listener_role_ids: scheduleHistoryFilters.listener_role_ids.join(),
  });
  const isLoading = isPeersLoading || isChatbotMessagesLoading;

  const filteredMessages = useMemo(() => {
    if (!chatbotMessages?.data) {
      return [];
    }

    return chatbotMessages.data.filter((message) => {
      const messageDate = message.created_at?.toString().split('T')[0] ?? '';
      const withinSelectedDates =
        messageDate >= scheduleHistoryFilters.start_date && messageDate <= scheduleHistoryFilters.end_date;

      if (!withinSelectedDates) {
        return false;
      }

      if (peerCrisisState === undefined) {
        return true;
      }

      return message.crisis_state === peerCrisisState;
    });
  }, [chatbotMessages?.data, peerCrisisState, scheduleHistoryFilters.end_date, scheduleHistoryFilters.start_date]);

  const [activeTab, setActiveTab] = useState<'messages' | 'summaries'>('messages');
  const [selectedChatId, setSelectedChatId] = useState<number | null>(null);

  useEffect(() => {
    if (selectedChatId === null) {
      return;
    }

    const chatStillVisible = filteredMessages.some((chat) => chat.id === selectedChatId);
    if (!chatStillVisible) {
      setSelectedChatId(null);
    }
  }, [filteredMessages, selectedChatId]);

  const visibleMessages = useMemo(() => {
    if (selectedChatId === null) {
      return filteredMessages;
    }

    return filteredMessages.filter((chat) => chat.id === selectedChatId);
  }, [filteredMessages, selectedChatId]);

  const handleViewChat = (chatId: number) => {
    setSelectedChatId(chatId);
    setActiveTab('messages');
  };

  const handleClearChatFilter = () => {
    setSelectedChatId(null);
  };

  return (
    <div>
      <Header heading="Peersona Chat Logs" />
      <SwitchBar heading={PEER_NAVIGATION} position={3} />
      {isLoading && <Spinner />}
      <ScheduleFilters
        setScheduleHistoryFilters={setScheduleHistoryFilters}
        scheduleHistoryFilters={scheduleHistoryFilters}
        listeners={peers?.data}
        listenerOrPeer="Peer"
        downloadCSV={false}
        setPeerCrisisState={setPeerCrisisState}
      />
      <div className="px-4 py-4">
        <div className="mb-4 flex gap-2">
          <button
            type="button"
            className={`rounded px-4 py-2 text-sm font-medium ${
              activeTab === 'messages'
                ? 'stacked-bar-blue-400 text-white shadow'
                : 'border border-gray-300 bg-white text-gray-700'
            }`}
            onClick={() => setActiveTab('messages')}
          >
            Chat History
          </button>
          <button
            type="button"
            className={`rounded px-4 py-2 text-sm font-medium ${
              activeTab === 'summaries'
                ? 'stacked-bar-blue-400 text-white shadow'
                : 'border border-gray-300 bg-white text-gray-700'
            }`}
            onClick={() => setActiveTab('summaries')}
          >
            Summaries
          </button>
        </div>

        {activeTab === 'messages' ? (
          <ChatbotMessageTable
            chatbotMessages={visibleMessages}
            selectedChatId={selectedChatId}
            onClearSelectedChat={selectedChatId !== null ? handleClearChatFilter : undefined}
          />
        ) : (
          <ChatbotSummaryTable chatbotMessages={filteredMessages} onViewChat={handleViewChat} />
        )}
      </div>
    </div>
  );
};

export default PeersonaChatLogs;
